/**
 * SLAM Sports AI - Design Tokens — UNC Carolina Athletics v3.0
 * ==============================================================
 * Canonical JavaScript source of truth for all design values.
 * Branded per Carolina Athletics Brand Identity Guidelines:
 *   Primary: Carolina Blue #7BAFD4 (Pantone 542C), White #FFFFFF
 *   Secondary: Navy Blue #13294B (Pantone 2767C), Pitch Black #2C2A29
 *
 * This file mirrors and extends the CSS variables in styles/design-tokens.css.
 * Use these tokens in:
 *   - Tailwind config (tailwind.config.js)
 *   - React components (inline styles, style props)
 *   - Utility functions
 *
 * IMPORTANT: When updating values here, also update styles/design-tokens.css
 * to keep CSS variables in sync.
 */

// =============================================================================
// BRAND COLORS — Carolina Blue System (Carolina Athletics Brand Identity)
// =============================================================================

const brandColors = {
  carolina: {
    DEFAULT: '#7BAFD4',       // Pantone 542C — primary brand color
    hover: '#6A9DC2',         // Darkened for hover states
    light: '#9DC5E0',         // Lightened for subtle accents
    dark: '#5A8FB2',          // Dark mode muted variant
    rgb: '123, 175, 212',
    muted: '#5A8FB2',         // Dimmed for subtle accents on dark bg
    dim: '#1A3448',           // Very dark tint for dark-mode card fills
  },
  navy: {
    DEFAULT: '#13294B',       // Pantone 2767C — dark contrast, headers
    light: '#1E3A5F',         // Lightened navy for dark-mode borders/cards
    dark: '#0A1725',          // Deepest navy
    rgb: '19, 41, 75',
  },
  black: {
    DEFAULT: '#000000',
    light: '#222222',
    rgb: '0, 0, 0',
  },
  white: '#FFFFFF',
};

// =============================================================================
// NEUTRAL / GRAY SCALE (OLED-optimized, darker end)
// =============================================================================

const neutralColors = {
  50: '#F9FAFB',
  100: '#F3F4F6',
  200: '#E5E7EB',
  300: '#D1D5DB',
  400: '#9CA3AF',
  500: '#6B7280',
  600: '#4B5563',
  700: '#374151',
  800: '#1F2937',
  900: '#111827',
};

// =============================================================================
// CAROLINA BLUE SCALE (accent palette based on #7BAFD4)
// =============================================================================

const carolinaBlueScale = {
  50: '#EBF4FA',
  100: '#D0E6F3',
  200: '#B0D5EA',
  300: '#8FC4E1',
  400: '#7BAFD4',    // Carolina Blue — primary
  500: '#6A9DC2',    // Hover / pressed
  600: '#5A8FB2',    // Muted
  700: '#4A7A9C',    // Dark accent
  800: '#2D5673',    // Very dark
  900: '#1A3448',    // Dim (dark-mode fills)
};

// =============================================================================
// NAVY SCALE (secondary palette for Tailwind)
// =============================================================================

const navyScale = {
  50: '#E8EDF4',
  100: '#C5D0E0',
  200: '#9FB2CC',
  300: '#7994B8',
  400: '#5376A4',
  500: '#2D5890',
  600: '#1E3A5F',    // Navy light
  700: '#13294B',    // Official Navy Blue
  800: '#0E1F3A',
  900: '#0A1725',    // Deepest navy
};

// =============================================================================
// PILLAR COLORS — Carolina-adapted (UNC brand-safe)
// =============================================================================

const pillarColors = {
  cog: '#7BAFD4',     // Cognitive Score — Carolina Blue
  char: '#C0C0C0',    // Character Score — Silver (unchanged)
  hdi: '#5A8FB2',     // HD Intelligence Score — Carolina Blue muted
  tools: '#E07C24',   // Tools Score (Baseball 4th pillar)
};

// =============================================================================
// TIER COLORS — Data-Earned Only (TDD Section 3.1)
// =============================================================================

const tierColors = {
  developing: '#FF4444',    // 0–39
  solid: '#7BAFD4',         // 40–69 — Carolina Blue
  elite: '#00CC66',         // 70–89
  generational: '#13294B',  // 90–100 — Navy Blue
};

// =============================================================================
// STATUS COLORS
// =============================================================================

const statusColors = {
  success: {
    DEFAULT: '#10B981',
    light: '#059669',
    foreground: '#FFFFFF',
  },
  warning: {
    DEFAULT: '#F59E0B',
    light: '#D97706',
    foreground: '#000000',
  },
  error: {
    DEFAULT: '#EF4444',
    light: '#DC2626',
    foreground: '#FFFFFF',
  },
  info: {
    DEFAULT: '#7BAFD4',
    light: '#5A8FB2',
    foreground: '#FFFFFF',
  },
};

// =============================================================================
// LEGACY COLORS (backward compatibility — aliases into brandColors)
// =============================================================================

const legacyColors = {
  carolina: {
    DEFAULT: brandColors.carolina.DEFAULT,
    digital: '#007FAE',
    muted: brandColors.carolina.muted,
    dim: brandColors.carolina.dim,
  },
  navy: {
    DEFAULT: brandColors.navy.DEFAULT,
    light: brandColors.navy.light,
    dark: brandColors.navy.dark,
  },
  pitchBlack: '#2C2A29',  // Pantone Process Black C
  silver: '#8D9092',      // Pantone 877 C Metallic
  flatGrey: '#A2AAAD',    // Pantone 429 C
};

// =============================================================================
// OLED BACKGROUND LAYERS (TDD Section 3.1)
// =============================================================================

const darkBackgrounds = {
  background: '#000000',    // --bg-base
  surface: '#0A0A0A',       // --bg-surface-1
  surfaceHover: '#111111',  // --bg-surface-2
  elevated: '#1A1A1A',      // --bg-surface-3
  surface4: '#222222',      // --bg-surface-4
};

// =============================================================================
// GLASSMORPHISM TOKENS — OLED
// =============================================================================

const glass = {
  dark: {
    background: 'rgba(10, 10, 10, 0.8)',
    border: 'rgba(255, 255, 255, 0.06)',
    blur: '12px',
    shadow: '0 8px 32px rgba(0, 0, 0, 0.6)',
  },
};

// =============================================================================
// AMBIENT GLOW EFFECTS — Carolina Blue #7BAFD4
// =============================================================================

const ambientGlow = {
  carolina: 'rgba(123, 175, 212, 0.08)',
  carolinaSubtle: 'rgba(123, 175, 212, 0.04)',
  carolinaIntense: 'rgba(123, 175, 212, 0.12)',
  // Legacy aliases
  gold: 'rgba(123, 175, 212, 0.08)',
  goldSubtle: 'rgba(123, 175, 212, 0.04)',
  goldIntense: 'rgba(123, 175, 212, 0.12)',
};

// =============================================================================
// TEXT COLORS (TDD Section 3.1)
// =============================================================================

const textColors = {
  dark: {
    primary: '#F5F5F5',
    secondary: '#999999',
    muted: '#555555',        // --text-tertiary
    quaternary: '#333333',   // --text-quaternary
    onPrimary: '#000000',
  },
};

// =============================================================================
// BORDER COLORS (TDD Section 3.1)
// =============================================================================

const borderColors = {
  dark: {
    DEFAULT: '#1A1A1A',      // --border-default
    light: '#252525',        // --border-light
    subtle: '#141414',       // --border-subtle
  },
};

// =============================================================================
// SPACING SCALE
// =============================================================================

const spacing = {
  0: '0',
  px: '1px',
  0.5: '0.125rem',   // 2px
  1: '0.25rem',       // 4px
  1.5: '0.375rem',    // 6px
  2: '0.5rem',        // 8px
  2.5: '0.625rem',    // 10px
  3: '0.75rem',       // 12px
  3.5: '0.875rem',    // 14px
  4: '1rem',          // 16px
  5: '1.25rem',       // 20px
  6: '1.5rem',        // 24px
  7: '1.75rem',       // 28px
  8: '2rem',          // 32px
  9: '2.25rem',       // 36px
  10: '2.5rem',       // 40px
  11: '2.75rem',      // 44px
  12: '3rem',         // 48px
  14: '3.5rem',       // 56px
  16: '4rem',         // 64px
  18: '4.5rem',       // 72px
  20: '5rem',         // 80px
  24: '6rem',         // 96px
  28: '7rem',         // 112px
  32: '8rem',         // 128px
  36: '9rem',         // 144px
  40: '10rem',        // 160px
  44: '11rem',        // 176px
  48: '12rem',        // 192px
  52: '13rem',        // 208px
  56: '14rem',        // 224px
  60: '15rem',        // 240px
  64: '16rem',        // 256px
  72: '18rem',        // 288px
  80: '20rem',        // 320px
  88: '22rem',        // 352px
  96: '24rem',        // 384px
  128: '32rem',       // 512px
};

// TDD Semantic spacing aliases (Section 3.3)
const spacingAliases = {
  xs: '4px',          // spacing[1]
  sm: '8px',          // spacing[2]
  md: '12px',         // spacing[3]
  lg: '16px',         // spacing[4]
  xl: '24px',         // spacing[6]
  '2xl': '32px',      // spacing[8]
  '3xl': '48px',      // spacing[12]
  '4xl': '64px',      // spacing[16]
};

// =============================================================================
// TOUCH TARGETS (WCAG 2.5.5)
// =============================================================================

const touchTargets = {
  min: '44px',
  sm: '40px',
  lg: '48px',
};

// =============================================================================
// TYPOGRAPHY (TDD Section 3.2)
// =============================================================================

const fontFamily = {
  sans: ['Inter', '-apple-system', 'BlinkMacSystemFont', 'Segoe UI', 'Roboto', 'sans-serif'],
  mono: ['JetBrains Mono', 'Fira Code', 'Consolas', 'monospace'],
};

const fontSize = {
  xs: '0.75rem',      // 12px
  sm: '0.875rem',     // 14px
  base: '1rem',       // 16px
  lg: '1.125rem',     // 18px
  xl: '1.25rem',      // 20px
  '2xl': '1.5rem',    // 24px
  '3xl': '2rem',      // 32px
  '4xl': '2.5rem',    // 40px
  '5xl': '3rem',      // 48px
};

const fontWeight = {
  thin: '200',         // TDD hero numerics
  light: '300',        // TDD headings
  normal: '400',
  medium: '500',
  semibold: '600',
  bold: '700',
};

const lineHeight = {
  tight: '1.25',
  normal: '1.5',
  relaxed: '1.75',
};

// TDD Typography compositions (Section 3.2)
const typography = {
  // TDD Named Styles
  hero: { size: '2.5rem', weight: '200', lineHeight: '1.1', letterSpacing: '-2px' },
  largeNum: { size: '1.75rem', weight: '200', lineHeight: '1.1', letterSpacing: '-1.5px' },
  heading: { size: '1.25rem', weight: '300', lineHeight: '1.4', letterSpacing: '-0.5px' },
  subheading: { size: '0.875rem', weight: '500', lineHeight: '1.3', letterSpacing: '0' },
  body: { size: '0.8125rem', weight: '400', lineHeight: '1.5', letterSpacing: '0' },
  caption: { size: '0.6875rem', weight: '500', lineHeight: '1.3', letterSpacing: '0.3px' },
  label: { size: '0.5rem', weight: '600', lineHeight: '1', letterSpacing: '1.5px', textTransform: 'uppercase' },
  mono: { size: '0.875rem', weight: '500', lineHeight: '1.3', letterSpacing: '0' },

  // Page-level headings (backward compat)
  h1: { size: '2.25rem', weight: '300', lineHeight: '2.5rem' },
  h2: { size: '1.875rem', weight: '300', lineHeight: '2.25rem' },
  h3: { size: '1.5rem', weight: '300', lineHeight: '2rem' },
  h4: { size: '1.25rem', weight: '500', lineHeight: '1.75rem' },
  h5: { size: '1rem', weight: '500', lineHeight: '1.5rem' },
  h6: { size: '0.875rem', weight: '500', lineHeight: '1.25rem' },

  // Tailwind-named typography scales
  pageTitle: { size: '1.5rem', weight: '300', lineHeight: '2rem', letterSpacing: '-0.5px' },
  sectionTitle: { size: '1.25rem', weight: '300', lineHeight: '1.75rem', letterSpacing: '-0.5px' },
  cardTitle: { size: '1rem', weight: '500', lineHeight: '1.5rem' },

  // Body text
  bodyLg: { size: '1.125rem', weight: '400', lineHeight: '1.75rem' },
  bodySm: { size: '0.875rem', weight: '400', lineHeight: '1.25rem' },

  // Labels & captions
  overline: { size: '0.5625rem', weight: '600', lineHeight: '1rem', letterSpacing: '1.5px', textTransform: 'uppercase' },
};

// =============================================================================
// BORDER RADIUS (TDD Section 3.4)
// =============================================================================

const borderRadius = {
  none: '0',
  sm: '6px',                 // --radius-sm
  DEFAULT: '10px',           // --radius-md
  md: '10px',                // --radius-md
  lg: '14px',                // --radius-lg
  xl: '16px',                // --radius-xl
  '2xl': '20px',
  full: '9999px',
  pill: '100px',             // --radius-pill
};

// =============================================================================
// BOX SHADOWS — OLED (deeper blacks)
// =============================================================================

const boxShadow = {
  sm: '0 1px 2px 0 rgb(0 0 0 / 0.2)',
  card: '0 1px 3px 0 rgb(0 0 0 / 0.3), 0 1px 2px -1px rgb(0 0 0 / 0.2)',
  cardHover: '0 4px 6px -1px rgb(0 0 0 / 0.3), 0 2px 4px -2px rgb(0 0 0 / 0.2)',
  elevated: '0 10px 15px -3px rgb(0 0 0 / 0.3), 0 4px 6px -4px rgb(0 0 0 / 0.2)',
  glowGold: '0 0 20px rgba(123, 175, 212, 0.3), 0 0 40px rgba(123, 175, 212, 0.15)',
  glowGoldIntense: '0 0 30px rgba(123, 175, 212, 0.4), 0 0 60px rgba(123, 175, 212, 0.2)',
  glass: '0 8px 32px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.03)',
};

// =============================================================================
// MOTION TOKENS (TDD Section 3.5)
// =============================================================================

const motion = {
  easeOut: 'cubic-bezier(.16, 1, .3, 1)',
  durationFast: '150ms',
  durationNormal: '300ms',
  durationScore: '1200ms',
};

// =============================================================================
// TRANSITIONS
// =============================================================================

const transitions = {
  fast: '150ms ease',
  normal: '200ms ease',
  slow: '300ms ease',
};

// =============================================================================
// Z-INDEX SCALE
// =============================================================================

const zIndex = {
  dropdown: 10,
  sticky: 20,
  fixed: 30,
  modalBackdrop: 40,
  modal: 50,
  popover: 60,
  tooltip: 70,
};

// =============================================================================
// LAYOUT DIMENSIONS (TDD Section 4.1.1)
// =============================================================================

const layout = {
  headerHeight: '64px',
  sidebarWidthExpanded: '200px',   // TDD: 200px (was 256px)
  sidebarWidthCollapsed: '56px',   // TDD: 56px (was 80px)
  sidebarWidthIcon: '48px',
  contentPadding: '1.25rem',
  contentMaxHeight: 'calc(100vh - 64px)',
};

// =============================================================================
// BREAKPOINTS (matches tailwind.config.js)
// =============================================================================

const breakpoints = {
  xs: '375px',
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
};

// =============================================================================
// RADIX / SHADCN SEMANTIC COLORS (HSL values) — OLED only
// =============================================================================

const radixSemanticHSL = {
  dark: {
    background: '0 0% 0%',
    foreground: '0 0% 96%',
    card: '0 0% 4%',
    cardForeground: '0 0% 96%',
    popover: '0 0% 4%',
    popoverForeground: '0 0% 96%',
    primary: '205 50% 66%',          // Carolina Blue #7BAFD4
    primaryForeground: '0 0% 0%',
    secondary: '214 60% 18%',        // Navy Blue #13294B
    secondaryForeground: '0 0% 96%',
    muted: '0 0% 10%',
    mutedForeground: '0 0% 60%',
    accent: '205 50% 66%',           // Carolina Blue
    accentForeground: '0 0% 0%',
    destructive: '0 84% 60%',
    destructiveForeground: '0 0% 98%',
    border: '0 0% 10%',
    input: '0 0% 7%',
    ring: '205 50% 66%',             // Carolina Blue
  },
};

// =============================================================================
// TEAM THEMING DEFAULTS
// =============================================================================

const teamDefaults = {
  primary: brandColors.carolina.DEFAULT,
  primaryHover: brandColors.carolina.hover,
  secondary: brandColors.navy.DEFAULT,
  accent: brandColors.carolina.DEFAULT,
  primaryRgb: brandColors.carolina.rgb,
  secondaryRgb: brandColors.navy.rgb,
  accentRgb: brandColors.carolina.rgb,
};

// =============================================================================
// TEAM OVERRIDES
// =============================================================================

const teamOverrides = {
  unc: {
    primary: '#7BAFD4',
    primaryHover: '#6A9DC2',
    secondary: '#13294B',
    accent: '#9DC5E0',
    primaryRgb: '123, 175, 212',
    backgroundDark: '#000000',
    surfaceDark: '#0A0A0A',
    surfaceHoverDark: '#111111',
    borderDark: 'rgba(123, 175, 212, 0.15)',
    primaryHSL: '205 50% 66%',
  },
  orioles: {
    primary: '#DF4601',
    primaryHover: '#c73e01',
    secondary: '#000000',
    accent: '#DF4601',
    primaryRgb: '223, 70, 1',
    backgroundDark: '#000000',
    surfaceDark: '#0A0A0A',
    surfaceHoverDark: '#111111',
    borderDark: 'rgba(223, 70, 1, 0.15)',
    primaryHSL: '19 99% 44%',
  },
};

// =============================================================================
// SIDEBAR SHARED TOKENS (TDD Section 4.1.1)
// =============================================================================

const sidebar = {
  width: {
    expanded: '200px',       // TDD: 200px (was 256px)
    collapsed: '56px',       // TDD: 56px (was 80px)
    icon: '48px',
  },
  section: {
    fontSize: '0.5625rem',          // 9px — TDD type-label
    fontWeight: '600',
    letterSpacing: '2px',           // TDD: 2px letter-spacing
    textTransform: 'uppercase',
    paddingX: '1rem',
    paddingY: '0.5rem',
  },
  item: {
    fontSize: '0.875rem',          // text-sm
    fontWeight: '500',
    paddingX: '0.75rem',
    paddingY: '0.5rem',
    borderRadius: '10px',          // --radius-md
    gap: '0.75rem',
    iconSize: 18,
  },
  active: {
    borderLeft: '2px solid #7BAFD4',
    textColor: '#7BAFD4',
    background: 'rgba(123, 175, 212, 0.15)',
  },
  dark: {
    background: '#000000',         // OLED black
    text: '#999999',               // --text-secondary
    textHover: '#F5F5F5',          // --text-primary
    hoverBg: 'rgba(255, 255, 255, 0.04)',
    sectionText: '#333333',        // --text-quaternary
    sectionTextHover: '#555555',   // --text-tertiary
    border: '#1A1A1A',             // --border-default
    activeBg: 'rgba(123, 175, 212, 0.15)',
    activeText: '#7BAFD4',
  },
};

// =============================================================================
// COMBINED TOKENS EXPORT (for convenience)
// =============================================================================

const tokens = {
  colors: {
    brand: brandColors,
    neutral: neutralColors,
    neon: carolinaBlueScale,
    carolina: carolinaBlueScale,
    navy: navyScale,
    pillar: pillarColors,
    tier: tierColors,
    status: statusColors,
    legacy: legacyColors,
    text: textColors,
    border: borderColors,
    darkBg: darkBackgrounds,
    glass,
    ambientGlow,
    radixSemantic: radixSemanticHSL,
    team: { defaults: teamDefaults, overrides: teamOverrides },
  },
  spacing,
  spacingAliases,
  touchTargets,
  typography,
  fontFamily,
  fontSize,
  fontWeight,
  lineHeight,
  borderRadius,
  boxShadow,
  motion,
  transitions,
  zIndex,
  layout,
  breakpoints,
  sidebar,
};

// =============================================================================
// MODULE EXPORTS
// Uses module.exports for CJS compatibility (tailwind.config.js runs in Node)
// Webpack/CRA interop handles this for ESM imports in React components
// =============================================================================

module.exports = tokens;

// Named exports for destructured imports: import { brandColors } from '../tokens/design-tokens'
module.exports.brandColors = brandColors;
module.exports.neutralColors = neutralColors;
module.exports.carolinaBlueScale = carolinaBlueScale;
module.exports.navyScale = navyScale;
// Legacy aliases
module.exports.neonGoldScale = carolinaBlueScale;
module.exports.yellowScale = carolinaBlueScale;
module.exports.pillarColors = pillarColors;
module.exports.tierColors = tierColors;
module.exports.statusColors = statusColors;
module.exports.legacyColors = legacyColors;
module.exports.darkBackgrounds = darkBackgrounds;
module.exports.glass = glass;
module.exports.ambientGlow = ambientGlow;
module.exports.textColors = textColors;
module.exports.borderColors = borderColors;
module.exports.spacing = spacing;
module.exports.spacingAliases = spacingAliases;
module.exports.touchTargets = touchTargets;
module.exports.fontFamily = fontFamily;
module.exports.fontSize = fontSize;
module.exports.fontWeight = fontWeight;
module.exports.lineHeight = lineHeight;
module.exports.typography = typography;
module.exports.borderRadius = borderRadius;
module.exports.boxShadow = boxShadow;
module.exports.motion = motion;
module.exports.transitions = transitions;
module.exports.zIndex = zIndex;
module.exports.layout = layout;
module.exports.breakpoints = breakpoints;
module.exports.radixSemanticHSL = radixSemanticHSL;
module.exports.teamDefaults = teamDefaults;
module.exports.teamOverrides = teamOverrides;
module.exports.sidebar = sidebar;
